function downsampleBuffer(buffer, srcRate, dstRate) {
  if (dstRate === srcRate) {
    return buffer;
  }
  const ratio = srcRate / dstRate;
  const newLen = Math.floor(buffer.length / ratio);
  const result = new Float32Array(newLen);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < newLen) {
    const nextOffset = Math.floor((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffset && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetBuffer = nextOffset;
  }
  return result;
}

function floatTo16BitPCM(float32) {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

class PCMRecorder {
  constructor() {
    this.stream = null;
    this.context = null;
    this.source = null;
    this.processor = null;
    this.onChunk = null;
  }

  async start(onChunk) {
    this.onChunk = onChunk;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    try {
      this.context = new AudioContextClass({ sampleRate: 16000 });
    } catch (e) {
      this.context = new AudioContextClass();
    }

    if (this.context.state === "suspended") {
      await this.context.resume();
    }

    this.source = this.context.createMediaStreamSource(this.stream);
    this.processor = this.context.createScriptProcessor(4096, 1, 1);
    const nativeRate = this.context.sampleRate;

    this.processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const down = nativeRate === 16000 ? input : downsampleBuffer(input, nativeRate, 16000);
      const pcm16 = floatTo16BitPCM(down);
      if (this.onChunk) {
        this.onChunk(pcm16.buffer);
      }
    };

    this.source.connect(this.processor);
    this.processor.connect(this.context.destination);
  }

  async stop() {
    try {
      this.processor && this.processor.disconnect();
    } catch (e) {
      // ignore cleanup errors
    }
    try {
      this.source && this.source.disconnect();
    } catch (e) {
      // ignore cleanup errors
    }
    try {
      this.stream && this.stream.getTracks().forEach((track) => track.stop());
    } catch (e) {
      // ignore cleanup errors
    }
    try {
      if (this.context && this.context.state !== "closed") {
        await this.context.close();
      }
    } catch (e) {
      // ignore cleanup errors
    }
    this.processor = null;
    this.source = null;
    this.stream = null;
    this.context = null;
    this.onChunk = null;
  }
}

class ASRClient {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this.ready = false;
    this.onPartial = null;
    this.onFinal = null;
    this.onError = null;
    this.finalBuffer = "";
    this.stopResolver = null;
    this.stopTimer = null;
    this.openResolved = false;
    this.stopping = false;
  }

  open() {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);
      } catch (e) {
        reject(e);
        return;
      }
      this.ws.binaryType = "arraybuffer";
      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({ type: "start" }));
      };
      this.ws.onmessage = (event) => {
        if (typeof event.data !== "string") {
          return;
        }
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch (e) {
          return;
        }
        if (msg.type === "ready") {
          this.ready = true;
          this.openResolved = true;
          resolve();
        } else if (msg.type === "partial") {
          this.onPartial && this.onPartial(msg.text || "");
        } else if (msg.type === "final") {
          if (msg.text) {
            this.finalBuffer = msg.text;
          }
          this.onFinal && this.onFinal(msg.text || "");
          this.resolveStopWait();
        } else if (msg.type === "error") {
          this.onError && this.onError(msg.message || "识别错误");
        } else if (msg.type === "closed") {
          this.resolveStopWait();
        }
      };
      this.ws.onerror = () => {
        if (this.stopping) {
          return;
        }
        if (!this.openResolved) {
          reject(new Error("ws error"));
        }
      };
      this.ws.onclose = () => {
        this.ready = false;
        this.resolveStopWait();
      };
    });
  }

  send(buffer) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.ready) {
      this.ws.send(buffer);
    }
  }

  resolveStopWait() {
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    if (this.stopResolver) {
      const resolver = this.stopResolver;
      this.stopResolver = null;
      resolver((this.finalBuffer || "").trim());
    }
  }

  async stopAndDrain(timeoutMs = 1200) {
    if (!this.ws) {
      return (this.finalBuffer || "").trim();
    }
    this.stopping = true;
    const currentWs = this.ws;
    if (currentWs.readyState === WebSocket.CLOSING || currentWs.readyState === WebSocket.CLOSED) {
      this.stopping = false;
      return (this.finalBuffer || "").trim();
    }

    const waitResult = new Promise((resolve) => {
      this.stopResolver = resolve;
      this.stopTimer = setTimeout(() => {
        this.stopTimer = null;
        if (this.stopResolver) {
          const resolver = this.stopResolver;
          this.stopResolver = null;
          resolver((this.finalBuffer || "").trim());
        }
      }, timeoutMs);
    });

    try {
      currentWs.send(JSON.stringify({ type: "stop" }));
    } catch (e) {
      this.resolveStopWait();
    }

    const text = await waitResult;
    try {
      currentWs.close();
    } catch (e) {
      // ignore cleanup errors
    }
    this.ws = null;
    this.ready = false;
    this.stopping = false;
    return text;
  }

  close() {
    this.stopping = true;
    this.resolveStopWait();
    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "stop" }));
      }
    } catch (e) {
      // ignore cleanup errors
    }
    try {
      this.ws && this.ws.close();
    } catch (e) {
      // ignore cleanup errors
    }
    this.ws = null;
    this.ready = false;
    this.stopping = false;
  }
}

function appendQueryParams(url, params = {}) {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value);
    }
  });
  const queryString = query.toString();
  if (!queryString) {
    return url;
  }
  return `${url}${url.indexOf("?") > -1 ? "&" : "?"}${queryString}`;
}

function getAsrWsUrl(params) {
  const isLocal =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const wsUrl = isLocal
    ? "ws://127.0.0.1:8000/api/v1/leadsqa/ws/asr"
    : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${
      window.location.host
    }/api/v1/leadsqa/ws/asr`;
  const urlWithParams = appendQueryParams(wsUrl, params);
  console.log("wsUrl:", urlWithParams);
  return urlWithParams;
}

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
];

const ALLOWED_IMAGE_EXT = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

export function validateImageFile(file) {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      message: `文件大小超过限制（最大 15MB）`,
    };
  }

  const mimeType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  // Check if it's an image file (by MIME type or extension)
  const isImageFile = mimeType.startsWith("image/") ||
    ALLOWED_IMAGE_EXT.some((ext) => fileName.endsWith(ext));

  if (!isImageFile) {
    return {
      valid: false,
      message: `只支持上传图片文件，支持的格式：${ALLOWED_IMAGE_EXT.join(", ")}`,
    };
  }

  // It's an image file, check if the format is supported
  if (mimeType && ALLOWED_IMAGE_TYPES.includes(mimeType)) {
    return { valid: true };
  }

  const hasValidExt = ALLOWED_IMAGE_EXT.some((ext) => fileName.endsWith(ext));
  if (hasValidExt) {
    return { valid: true };
  }

  return {
    valid: false,
    message: `上传的图片包含暂未支持的格式，请转换格式后重试。支持的图片文件格式：${ALLOWED_IMAGE_EXT.join(", ")}`,
  };
}

export class ASRSession {
  constructor({ sendId, source, onPartial, onFinal, onError }) {
    this.client = new ASRClient(
      getAsrWsUrl({
        send_id: sendId,
        source,
      })
    );
    this.recorder = new PCMRecorder();
    this.finalizedText = "";
    this.hasAudio = false;
    this.client.onPartial = (text) => {
      onPartial && onPartial((this.finalizedText + (text || "")).trim());
    };
    this.client.onFinal = (text) => {
      if (text) {
        this.finalizedText = (this.finalizedText + text).trim() + " ";
        onFinal && onFinal(this.finalizedText.trim());
      }
    };
    this.client.onError = onError;
  }

  async start() {
    await this.client.open();
    await this.recorder.start((buffer) => {
      this.hasAudio = true;
      this.client.send(buffer);
    });
  }

  async stop() {
    await this.recorder.stop();
    const text = await this.client.stopAndDrain(1200);
    return {
      text: text || this.finalizedText.trim(),
      hasAudio: this.hasAudio,
    };
  }

  close() {
    this.recorder.stop().catch(() => {});
    this.client.close();
  }
}

const OSS_BASE_URL = (process.env.VUE_APP_OSS_URL || "https://dq123oss.dq123.com").replace(/\/+$/, "") + "/";

export async function uploadLeadsqaImage(file, params = {}) {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", "image");
  if (params.sendId !== undefined && params.sendId !== null && params.sendId !== "") {
    form.append("send_id", params.sendId);
  }
  if (params.source !== undefined && params.source !== null && params.source !== "") {
    form.append("source", params.source);
  }
  const response = await fetch("/api/v1/leadsqa/upload", {
    method: "POST",
    body: form,
  });
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    // non-json responses are handled by status and URL checks below
  }
  if (!response.ok) {
    throw new Error((data && (data.message || data.error)) || "图片上传失败");
  }
  // 兼容返回格式：data 可能是 { file_name, kind, oss_path, size } 或嵌套在 data 字段中
  const fileInfo =
    data &&
    (data.oss_path
      ? data
      : data.data && data.data.oss_path
        ? data.data
        : null);
  if (!fileInfo || !fileInfo.oss_path) {
    throw new Error("图片上传返回缺少文件信息");
  }
  // 拼接 OSS 域名生成可访问的 URL
  fileInfo.url = OSS_BASE_URL + fileInfo.oss_path.replace(/^\/+/, '');
  return fileInfo;
}
